import {
  type FinishEnum,
  NONFOIL_FINISH,
} from "modules/cards/model/card-enums";
import type {
  GameDefinition,
  GameFinishDefinition,
} from "modules/common/model/models";

export type FinishSpec = GameFinishDefinition;

const finishSpecsById = new Map<string, FinishSpec>();

function orderedFinishSpecs(): FinishSpec[] {
  return [...finishSpecsById.values()].sort(
    (left, right) => left.order - right.order
  );
}

function genericFinishSpec(value: string): FinishSpec {
  return {
    value,
    label: value
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase()),
    surface: true,
    foilVisual: false,
    order: Number.MAX_SAFE_INTEGER,
  };
}

export function priceFinishes(): FinishEnum[] {
  return orderedFinishSpecs()
    .filter((spec) => spec.priceKey !== undefined)
    .map((spec) => spec.value as FinishEnum);
}

type FinishDefinitions = ReadonlyArray<Pick<GameDefinition, "id" | "finishes">>;

export function finishSpecsForDefinitions(
  definitions: FinishDefinitions
): FinishSpec[] {
  finishSpecsById.clear();
  for (const definition of definitions) {
    for (const finish of definition.finishes) {
      finishSpecsById.set(finish.value, finish);
    }
  }
  return orderedFinishSpecs();
}

export function finishSpecsForGame(
  definition: Pick<GameDefinition, "finishes">
): FinishSpec[] {
  return [...definition.finishes].sort(
    (left, right) => left.order - right.order
  );
}

export function finishSpec(finish: string): FinishSpec {
  return finishSpecsById.get(finish) ?? genericFinishSpec(finish);
}

export function finishLabel(finish: string): string {
  return finishSpec(finish)?.label ?? finish;
}

/**
 * Finishes that are intrinsic to a printing rather than optional surface
 * choices. A lone surface is required when nonfoil is unavailable; modifiers
 * such as Signed are always retained alongside the selected surface.
 */
export function requiredFinishesForAvailability(
  available: readonly FinishEnum[]
): FinishEnum[] {
  const availableSet = new Set<string>(available);
  const availableSpecs = available.map((finish) => finishSpec(finish));
  const surfaces = availableSpecs.filter(
    (spec) => spec.surface && availableSet.has(spec.value)
  );
  const modifiers = availableSpecs.filter(
    (spec) =>
      !spec.surface &&
      spec.value !== NONFOIL_FINISH &&
      availableSet.has(spec.value)
  );

  return [
    ...(availableSet.has(NONFOIL_FINISH) || surfaces.length !== 1
      ? []
      : [surfaces[0].value as FinishEnum]),
    ...modifiers.map((spec) => spec.value as FinishEnum),
  ];
}

export function includeRequiredFinishes(
  selected: readonly FinishEnum[],
  available: readonly FinishEnum[]
): FinishEnum[] {
  const selectedSet = new Set<string>([
    ...selected,
    ...requiredFinishesForAvailability(available),
  ]);
  const ordered = orderedFinishSpecs()
    .filter((spec) => selectedSet.delete(spec.value))
    .map((spec) => spec.value as FinishEnum);

  return [...ordered, ...selectedSet] as FinishEnum[];
}

export function hasFoilVisual(finishes: readonly string[]): boolean {
  return finishHasFoilVisual(primaryAvailableFinish(finishes));
}

export function finishHasFoilVisual(finish: string | undefined): boolean {
  return finish ? finishSpec(finish).foilVisual === true : false;
}

export function primaryDisplayFinish(
  finishes: readonly string[]
): FinishEnum | undefined {
  return (
    (orderedFinishSpecs().find(
      (spec) => spec.value !== NONFOIL_FINISH && finishes.includes(spec.value)
    )?.value as FinishEnum) ??
    (finishes.find((finish) => finish !== NONFOIL_FINISH) as
      | FinishEnum
      | undefined)
  );
}

export function primaryAvailableFinish(
  finishes: readonly string[]
): FinishEnum | undefined {
  if (finishes.includes(NONFOIL_FINISH)) return NONFOIL_FINISH;
  return (
    (orderedFinishSpecs().find((spec) => finishes.includes(spec.value))
      ?.value as FinishEnum | undefined) ??
    (finishes[0] as FinishEnum | undefined)
  );
}

export function priceForFinish(
  price: {
    default: number;
    nonfoil?: number;
    foil?: number;
    metal?: number;
    metalDeluxe?: number;
    etched?: number;
  },
  finish: FinishEnum
): number {
  const key = finishSpec(finish).priceKey;
  return key ? price[key] || 0 : price.default || 0;
}
